import { useEffect, useState } from 'react';
import { fetchPage, type PageDetail } from '../../api/knowledge';
import './NodeDetailPanel.css';

interface Props {
  nodeId: string;
  onClose: () => void;
}

export function NodeDetailPanel({ nodeId, onClose }: Props) {
  const [detail, setDetail] = useState<PageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    fetchPage(nodeId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [nodeId]);

  return (
    <aside className="node-detail">
      <header className="node-detail__header">
        <div className="node-detail__title">{detail?.title ?? 'Loading...'}</div>
        <button type="button" onClick={onClose} className="node-detail__close" aria-label="Close">
          ×
        </button>
      </header>
      {error && <div className="node-detail__error">{error}</div>}
      {detail && (
        <div className="node-detail__body">
          <section className="node-detail__meta">
            {Object.entries(detail.meta)
              .filter(([key]) => !key.startsWith('_') && key !== 'sources')
              .map(([key, value]) => (
                <div key={key} className="node-detail__meta-row">
                  <span className="label">{key}</span>
                  <span className="node-detail__meta-value">{formatValue(value)}</span>
                </div>
              ))}
          </section>
          {'sources' in detail.meta && (
            <section className="node-detail__section">
              <h4>Sources</h4>
              <SourcesList raw={detail.meta.sources} />
            </section>
          )}
          {detail.edges.length > 0 && (
            <section className="node-detail__section">
              <h4>Edges</h4>
              <ul className="node-detail__edges">
                {detail.edges.map((edge, i) => (
                  <li key={`${edge.target}-${i}`}>
                    <span className="node-detail__edge-type">{edge.type ?? 'related'}</span>
                    <span className="node-detail__edge-target">{edge.target}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="node-detail__section">
            <h4>Preview</h4>
            <pre className="node-detail__preview">{detail.content_preview}</pre>
          </section>
        </div>
      )}
    </aside>
  );
}

interface SourceEntry {
  ref: string;
  kind?: string;
  title?: string;
  scope?: string;
  approved_at?: string;
}

function SourcesList({ raw }: { raw: unknown }) {
  const entries = normalizeSources(raw);
  if (entries.length === 0) {
    return <div className="node-detail__sources-empty">No sources recorded.</div>;
  }
  return (
    <ul className="node-detail__sources">
      {entries.map((src, i) => (
        <SourceRow key={`${src.ref}-${i}`} source={src} />
      ))}
    </ul>
  );
}

function normalizeSources(raw: unknown): SourceEntry[] {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.flatMap((item) => {
    if (typeof item === 'string') {
      return item.trim() ? [{ ref: item.trim() }] : [];
    }
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const ref = obj.ref ?? obj.url ?? obj.id;
      if (typeof ref !== 'string' || !ref.trim()) return [];
      return [
        {
          ref: ref.trim(),
          kind: typeof obj.kind === 'string' ? obj.kind : undefined,
          title: typeof obj.title === 'string' ? obj.title : undefined,
          scope: typeof obj.scope === 'string' ? obj.scope : undefined,
          approved_at:
            typeof obj.approved_at === 'string' ? obj.approved_at : undefined,
        },
      ];
    }
    return [];
  });
}

function SourceRow({ source }: { source: SourceEntry }) {
  const isExternal =
    source.kind === 'external' ||
    source.ref.startsWith('http://') ||
    source.ref.startsWith('https://');
  const label = source.title?.trim() || source.ref;
  return (
    <li className="node-detail__source">
      <span className="node-detail__source-kind">{source.kind ?? 'ref'}</span>
      {isExternal ? (
        <a
          className="node-detail__source-link"
          href={source.ref}
          target="_blank"
          rel="noopener noreferrer"
          title={source.ref}
        >
          {label}
        </a>
      ) : (
        <span className="node-detail__source-text" title={source.ref}>
          {label}
        </span>
      )}
    </li>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  return JSON.stringify(value);
}
